import React from 'react';
import { Briefcase, Trophy, Code, Award, Sparkles } from 'lucide-react';
import experienceData from '../experienceData';
import { Card } from './ui/card';
import { ReactComponent as TorcIcon } from '../images/torc.svg';

const achievements = [
    {
        icon: TorcIcon,
        title: "Torc Ambassador",
        description: "Sharing what's happening behind the scenes like real discussions, community spaces, and events focused on growth and career clarity",
        type: "Volunteering",
        url: "https://platform.torc.dev/#/r/ZILxKHb0/cp",
    },
    {
        icon: Award,
        title: "Microsoft Certified: Azure AI Fundamentals",
        description: "Identify AI concepts and capabilities and Implement AI solutions with Microsoft Foundry",
        type: "Certification",
        url: "https://learn.microsoft.com/en-us/users/ameliadutta-3792/credentials/dcb2fa65301cd85f",
    },
    {
        icon: Trophy,
        title: "Frontend Challenge: Halloween Edition Winner!",
        description: "Won the Frontend Challenge: Halloween Edition's Perfect Landing Section!",
        type: "Competition",
        url: "https://dev.to/devteam/congrats-to-the-frontend-challenge-halloween-edition-winners-13pg",
    },
    {
        icon: Code,
        title: "Hacktoberfest Writing Challenge",
        description: "Won the Hacktoberfest Writing Challenge",
        type: "Open Source",
        url: "https://dev.to/devteam/congrats-to-the-2025-hacktoberfest-writing-challenge-winners-1hpn",
    },
    {
        icon: Award,
        title: "GenAI Bootcamp",
        description: "Gold Squad Certificate holder",
        type: "Certification",
        url: "https://www.linkedin.com/posts/ameliadutta_im-excited-to-share-that-ive-earned-the-activity-7321565035216953345-Ak_v?utm_source=share&utm_medium=member_desktop&rcm=ACoAACq2AcsB53x7dX84poMGtP-yHpo8xYeF_Ic",
    },
    {
        icon: Award,
        title: "AWS Certified Cloud Practitioner",
        description: "Certified in fundamental understanding of IT services and their uses in the AWS Cloud",
        type: "Certification",
        url: "https://www.credly.com/badges/7065297a-6dc9-412a-9e47-d70b6bd6951e/public_url",
    },
];

const Experience = () => {
    return (
        <section id="experience" className="w-full py-20 px-8 max-w-7xl mx-auto space-y-24">
            {/* Journey Header */}
            <h2 className="text-5xl font-bold text-center text-active mb-16 relative animate-fade-in">
                Journey
                <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-crimson rounded-full"></span>
            </h2>

            <div className="grid lg:grid-cols-2 gap-16">
                {/* Developer Experience */}
                <div className="space-y-8 animate-slide-in">
                    <h3 className="text-3xl font-bold text-active mb-8 flex items-center gap-3">
                        <Briefcase className="w-8 h-8 text-crimson" />
                        Dev Experience
                    </h3>

                    <div className="space-y-12">
                        {experienceData.map((item) => (
                            <div key={item.id} className="relative pl-8 border-l-2 border-crimson/20 pb-2 last:pb-0">
                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-crimson border-4 border-white shadow-sm"></div>
                                <h4 className="text-xl font-bold text-active">{item.role}</h4>
                                <p className="text-crimson font-medium mb-2">{item.company} | {item.date}</p>
                                <p className="text-sm text-gray-500 mb-2 italic">{item.location}</p>
                                <ul className="text-active/80 list-disc list-outside ml-4 space-y-1 mb-4">
                                    {item.description.map((desc, i) => (
                                        <li key={i}>{desc}</li>
                                    ))}
                                </ul>
                                <div className="flex flex-wrap gap-2">
                                    {item.skills.map((skill, index) => (
                                        <span key={index} className="px-2 py-1 text-xs font-medium bg-crimson/10 text-crimson rounded-full">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Beyond Code / Achievements */}
                <div className="space-y-8" id="achievements">
                    <div className="animate-fade-up">
                        <div className="flex items-center gap-3 mb-2">
                            <Sparkles className="w-8 h-8 text-nudeBlush" />
                            <h3 className="text-3xl font-bold text-active">
                                Beyond Code
                            </h3>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        {achievements.map((item, index) => (
                            <Card
                                key={item.title}
                                className="group p-5 bg-card border-border hover:border-primary/50 hover:shadow-card transition-all duration-300 animate-fade-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="flex flex-col gap-4">
                                    <div className="flex justify-between items-start">
                                        <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors shrink-0">
                                            <item.icon className="h-6 w-6 text-primary" />
                                        </div>
                                        <span className="text-[10px] font-bold text-primary/70 uppercase tracking-widest border border-primary/20 px-2 py-1 rounded-full">
                                            {item.type}
                                        </span>
                                    </div>

                                    <div>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                                            <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                                {item.title}
                                            </h3>
                                        </a>
                                        <p className="text-muted-foreground text-sm mt-1 line-clamp-2">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Experience;
